import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import Layout from "../../components/common/hoc/Layout";
import { useDispatch, useSelector } from "react-redux";
import CustomSpinner from "../../components/common/Spinner/CustomSpinner";
import TableContainer from "@material-ui/core/TableContainer";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import Checkbox from "@material-ui/core/Checkbox";
import Box from "@material-ui/core/Box";
import TablePagination from "@material-ui/core/TablePagination";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import DeleteIcon from "@material-ui/icons/Delete";
import IconButton from "@material-ui/core/IconButton";
import EditIcon from "@material-ui/icons/Edit";
import EnhancedTableHead from "../../components/common/table/EnhancedTableHead";
import Add from "./components/add";
import Edit from "./components/edit";
import NotFound from "../../components/common/notFound/NotFound";
import RecordChangeButtons from "../../components/common/recordChangeButtons/RecordChangeButtons";
import { useTranslation } from "react-i18next";
import { listContentManager } from "../../store/contentManager";
import { addContentManager } from "../../store/contentManager";
import { updateContentManager } from "../../store/contentManager";
import { deleteContentManager } from "../../store/contentManager";
import { showCustomConfirmationDialog } from "../../store/customConfirmationDialogReducer";
import { getComparator, stableSort } from "../../utils/tableFilter";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  paper: {
    width: "100%",
    marginBottom: theme.spacing(2),
  },
  table: {
    marginTop: "1rem",
    minWidth: 750,
  },
  rowEnable: {
    outlineColor: theme.palette.secondary.main,
    outlineWidth: "2px",
    outlineStyle: "solid",
  },
  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },
  buttons: {
    display: "flex",
    justifyContent: "flex-start",
    width: "100%",
  },
  buttonAdd: {
    marginLeft: theme.spacing(1),
  },
}));

const RegisterContentManagerScreen = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation("translation");
  const contentManagerReducer = useSelector(
    (state) => state.contentManagerReducer
  );
  const { loading, error, contentManagers } = contentManagerReducer;
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("");
  const [selected, setSelected] = useState([]);
  const [rowEnable, setRowEnable] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [email, setEmail] = useState("");
  const [isEnabledFormAddAdmin, setIsEnabledFormAddAdmin] = useState(false);
  const [isEnabledFormEditAdmin, setIsEnabledFormEditAdmin] = useState(false);
  const [currentContentManager, setCurrentContentManager] = useState();
  const headCells = [
    {
      id: "name",
      numeric: false,
      disablePadding: true,
      label: t("contentManager.headCells.name"),
    },
    {
      id: "email",
      numeric: false,
      disablePadding: false,
      label: t("contentManager.headCells.email"),
    },
    {
      id: "role",
      numeric: false,
      disablePadding: true,
      label: t("contentManager.headCells.role"),
    },
  ];
  const isSelected = (name) => selected.indexOf(name) !== -1;
  const emptyRows =
    rowsPerPage -
    Math.min(rowsPerPage, contentManagers.length - page * rowsPerPage);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = contentManagers.map((n) => n);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const toggleFormAdmin = () => {
    setIsEnabledFormAddAdmin((prev) => !prev);
    setIsEnabledFormEditAdmin(false);
  };

  const toggleFormEdit = (isEnable) => {
    setIsEnabledFormAddAdmin(false);
    setIsEnabledFormEditAdmin(isEnable);
  };

  const focusOnlastItemAdded = () => {
    setRowEnable(true);
    setTimeout(function () {
      setRowEnable(false);
    }, 3000);
  };

  const addHandler = async (formValues) => {
    setEmail(formValues.email);
    await dispatch(
      addContentManager(formValues.name, formValues.email, formValues.role)
    );
    await dispatch(listContentManager());
    setSelected([]);
    focusOnlastItemAdded();
  };

  const deleteHandler = (contentManager) => {
    const configDialog = {
      title: t("contentManager.deleteDialog.title"),
      message: `${t("contentManager.deleteDialog.firstMessage")} "${
        contentManager.name
      }", ${t("contentManager.deleteDialog.secondMessage")}`,
      onSubmit: async function () {
        await dispatch(deleteContentManager(contentManager))
          .then(async (result) => {
            dispatch({ type: "CUSTOM_CONFIRMATION_DIALOG_CLOSED" });
            await dispatch(listContentManager());
            setSelected([]);
          })
          .catch((err) => {
            dispatch({
              type: "ADMIN_DELETE_FAIL",
              payload: error,
            });
          });
      },

      close: async function () {
        await dispatch({ type: "CUSTOM_CONFIRMATION_DIALOG_CLOSED" });
      },
      buttons: {
        cancel: t("contentManager.deleteDialog.buttons.cancel"),
        confirm: t("contentManager.deleteDialog.buttons.confirm"),
      },
    };

    dispatch(
      showCustomConfirmationDialog({
        ...configDialog,
      })
    );
  };

  const editHandler = (contentManager) => {
    toggleFormEdit(false);
    setCurrentContentManager(contentManager);
    const configDialog = {
      title: t("contentManager.editDialog.title"),
      message: `${t("contentManager.editDialog.firstMessage")} "${
        contentManager.name
      }", ${t("contentManager.editDialog.secondMessage")}`,
      onSubmit: function () {
        toggleFormEdit(true);
      },
      close: async function () {
        await dispatch({ type: "CUSTOM_CONFIRMATION_DIALOG_CLOSED" });
      },
      buttons: {
        cancel: t("contentManager.editDialog.buttons.cancel"),
        confirm: t("contentManager.editDialog.buttons.confirm"),
      },
    };
    dispatch(
      showCustomConfirmationDialog({
        ...configDialog,
      })
    );
  };

  const updateHandler = async (formValues) => {
    setEmail(formValues.email);
    await dispatch(
      updateContentManager(
        currentContentManager?.id,
        formValues.name,
        formValues.email,
        formValues.role
      )
    );
    await dispatch(listContentManager());
    setSelected([]);
    focusOnlastItemAdded();
  };

  const formConfigI18n = {
    singleDeleteButton: t("contentManager.singleDeleteButton"),
    singleDeleteMultipleButton: t("contentManager.singleDeleteMultipleButton"),
    addForm: {
      title: t("contentManager.addForm.title"),
      cancelButton: t("contentManager.addForm.cancelButton"),
      confirmButton: t("contentManager.addForm.confirmButton"),
      name: {
        requiredErrorMsg: t("contentManager.addForm.name.requiredErrorMsg"),
        label: t("contentManager.addForm.name.label"),
        placeholder: t("contentManager.addForm.name.placeholder"),
      },
      email: {
        requiredErrorMsg: t("contentManager.addForm.email.requiredErrorMsg"),
        label: t("contentManager.addForm.email.label"),
        placeholder: t("contentManager.addForm.email.placeholder"),
      },
      role: {
        requiredErrorMsg: t("contentManager.addForm.role.requiredErrorMsg"),
        label: t("contentManager.addForm.role.label"),
        options: {
          xperienceContentTeam: {
            label: t(
              "contentManager.addForm.role.options.xperienceContentTeam.label"
            ),
          },
          externalContentCreator: {
            label: t(
              "contentManager.addForm.role.options.externalContentCreator.label"
            ),
          },
        },
      },
    },
    editForm: {
      title: t("contentManager.editForm.title"),
      cancelButton: t("contentManager.editForm.cancelButton"),
      confirmButton: t("contentManager.editForm.confirmButton"),
      name: {
        requiredErrorMsg: t("contentManager.editForm.name.requiredErrorMsg"),
        label: t("contentManager.editForm.name.label"),
        placeholder: t("contentManager.editForm.name.placeholder"),
      },
      email: {
        requiredErrorMsg: t("contentManager.editForm.email.requiredErrorMsg"),
        label: t("contentManager.editForm.email.label"),
        placeholder: t("contentManager.editForm.email.placeholder"),
      },
      role: {
        requiredErrorMsg: t("contentManager.editForm.role.requiredErrorMsg"),
        label: t("contentManager.editForm.role.label"),
        options: {
          xperienceContentTeam: {
            label: t(
              "contentManager.editForm.role.options.xperienceContentTeam.label"
            ),
          },
          externalContentCreator: {
            label: t(
              "contentManager.editForm.role.options.externalContentCreator.label"
            ),
          },
        },
      },
    },
  };

  useEffect(() => {
    dispatch(listContentManager());
  }, [dispatch]);

  return (
    <Layout title={t("contentManager.title")}>
      <Container disableGutters maxWidth={false}>
        <Box
          component="span"
          display={
            isEnabledFormAddAdmin || isEnabledFormEditAdmin ? "none" : "block"
          }
        >
          <Grid item xs={12} sm={12} lg={12}>
            <div className={classes.buttons}>
              <Button
                variant="contained"
                color="primary"
                disabled={loading}
                onClick={() => setIsEnabledFormAddAdmin((prev) => !prev)}
              >
                {t("contentManager.addButton")}
              </Button>
            </div>
          </Grid>
        </Box>

        <Add
          translateValidation={formConfigI18n.addForm}
          isEnabledFormAddAdmin={!isEnabledFormAddAdmin}
          loading={loading}
          addHandler={addHandler}
          show={toggleFormAdmin}
        />

        <Edit
          translateValidation={formConfigI18n.editForm}
          isEnabledFormEditAdmin={!isEnabledFormEditAdmin}
          loading={loading}
          editHandler={updateHandler}
          show={toggleFormEdit}
          currentContentManager={currentContentManager}
        />

        <>
          {loading ? (
            <CustomSpinner />
          ) : error ? (
            <NotFound
              title="Oops!"
              message="Ocorreu um erro, tente recarregar a página"
              image="/images/notfound.png"
              redirect="/app/content"
            />
          ) : (
            <div>
              <TableContainer>
                <Table
                  className={classes.table}
                  aria-labelledby="tableTitle"
                  size={"medium"}
                  aria-label="enhanced table"
                >
                  <EnhancedTableHead
                    headCells={headCells}
                    classes={classes}
                    numSelected={selected.length}
                    order={order}
                    orderBy={orderBy}
                    onSelectAllClick={handleSelectAllClick}
                    onRequestSort={handleRequestSort}
                    rowCount={contentManagers.length}
                  />
                  <TableBody>
                    {stableSort(contentManagers, getComparator(order, orderBy))
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      .map((row, index) => {
                        const isItemSelected = isSelected(row);
                        const labelId = `enhanced-table-checkbox-${index}`;

                        return (
                          <TableRow
                            hover
                            role="checkbox"
                            aria-checked={isItemSelected}
                            tabIndex={0}
                            key={row.id}
                            selected={isItemSelected}
                            className={
                              row.email === email && rowEnable
                                ? classes.rowEnable
                                : ""
                            }
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                onClick={(event) => handleClick(event, row)}
                                checked={isItemSelected}
                                inputProps={{ "aria-labelledby": labelId }}
                              />
                              <IconButton
                                aria-label="edit"
                                onClick={(event) => editHandler(row)}
                              >
                                <EditIcon color="secondary" />
                              </IconButton>
                            </TableCell>
                            <TableCell padding="none">{row.name}</TableCell>
                            <TableCell align="left">{row.email}</TableCell>
                            <TableCell align="left">{row.role}</TableCell>
                            <TableCell align="left">{row.cnpj}</TableCell>
                          </TableRow>
                        );
                      })}
                    {emptyRows > 0 && (
                      <TableRow style={{ height: 53 * emptyRows }}>
                        <TableCell colSpan={6} />
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box display="flex" p={1}>
                <Box p={1} flexGrow={1}>
                  <TablePagination
                    rowsPerPageOptions={[10, 25]}
                    component="div"
                    count={contentManagers.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onChangePage={handleChangePage}
                    onChangeRowsPerPage={handleChangeRowsPerPage}
                  />
                </Box>
                <Box p={1} flexGrow={1}>
                  <RecordChangeButtons
                    singleDeleteButton={formConfigI18n.singleDeleteButton}
                    singleDeleteMultipleButton={
                      formConfigI18n.singleDeleteMultipleButton
                    }
                    itensSelected={selected}
                    handlerSingle={deleteHandler}
                    icon={<DeleteIcon />}
                  />
                </Box>
              </Box>
            </div>
          )}
        </>
      </Container>
    </Layout>
  );
};

export { RegisterContentManagerScreen };
